import React from 'react';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutlined';
import { Box, Button, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import {
  RECEIVABLES_DATE_COLUMN_INDEXES,
  RECEIVABLES_TABLE_ARIA_LABEL,
  RECEIVABLES_TABLE_COLUMN_WIDTHS,
  RECEIVABLES_TABLE_HEADERS,
  RECEIVABLES_TABLE_MIN_WIDTH,
  RECEIVABLES_SECTION_TITLES,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/receivables/constants.ts';
import { ReceivableRow } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/receivables/ReceivableRow.tsx';
import { FinancialConditionTable } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/shared/FinancialConditionTable.tsx';
import type { IReceivablesSectionProps } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/components/receivables/types.ts';
import {
  BorrowerType,
  FinancialStatementForm,
  type TFinancialConditionFormValues,
} from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/types.ts';
import { REQUIRED_RECEIVABLE_TEMPLATE_CODES } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/constants.ts';
import { createReceivable } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/financialCondition/utils.ts';
import { documentGenerationFormStyles } from '~/modules/covenants/CovenantsDataApplicationForm/documentGeneration/styles.ts';

export const ReceivablesSection: React.FC<IReceivablesSectionProps> = ({ disabled }) => {
  const { control, watch } = useFormContext<TFinancialConditionFormValues>();
  const borrowerType = watch('borrowerType')?.value;
  const financialStatementForm = watch('financialStatementForm')?.value as
    FinancialStatementForm | undefined;
  const isLegalEntity = borrowerType === BorrowerType.LegalEntity;
  const { fields, append, remove } = useFieldArray({ control, name: 'receivables' });
  const canRemoveReceivable = (templateCode?: string) =>
    fields.length > 1 &&
    !REQUIRED_RECEIVABLE_TEMPLATE_CODES.includes(
      templateCode as (typeof REQUIRED_RECEIVABLE_TEMPLATE_CODES)[number]
    );
  const showActions = fields.some((field) => canRemoveReceivable(field.templateCode));

  if (!isLegalEntity || !financialStatementForm) {
    return null;
  }

  return (
    <Box sx={documentGenerationFormStyles.section}>
      <Typography component="h3" sx={documentGenerationFormStyles.sectionTitle}>
        {RECEIVABLES_SECTION_TITLES[financialStatementForm]}
      </Typography>
      <FinancialConditionTable
        ariaLabel={RECEIVABLES_TABLE_ARIA_LABEL}
        headers={[...RECEIVABLES_TABLE_HEADERS]}
        showActions={showActions}
        dateColumnIndexes={[...RECEIVABLES_DATE_COLUMN_INDEXES]}
        columnWidths={[...RECEIVABLES_TABLE_COLUMN_WIDTHS]}
        minWidth={RECEIVABLES_TABLE_MIN_WIDTH}
      >
        {fields.map((field, index) => (
          <ReceivableRow
            key={field.id}
            index={index}
            templateCode={field.templateCode}
            canRemove={canRemoveReceivable(field.templateCode)}
            disabled={disabled}
            onRemove={() => remove(index)}
          />
        ))}
      </FinancialConditionTable>
      <Button
        variant="outlined"
        startIcon={<AddCircleOutlineIcon />}
        onClick={() => append(createReceivable())}
        disabled={disabled}
        sx={documentGenerationFormStyles.addButton}
      >
        Додати заборгованість
      </Button>
    </Box>
  );
};
